import CheckIcon from "@mui/icons-material/Check";
import { Tooltip, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { FacilitiesWithCredentialsQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useEffect, useState } from "react";
import { FACILITIES } from "routes/Customer/Credentials/schema";

type Row = FacilitiesWithCredentialsQuery["facilities"]["items"][number];

const COLUMNS: OmnisearchGridColDef<Row>[] = [
  {
    field: "facility",
    headerName: "Facility",
    filterKeyType: "facility",
    flex: 2,
    valueGetter: (_, row) => {
      const { name, customerFacilityId } = row;

      return `${name}${customerFacilityId ? ` (${customerFacilityId})` : ""}`;
    },
  },
  {
    field: "streetAddress1",
    headerName: "Street Address",
    flex: 2,
    sortable: true,
    valueGetter(_, row) {
      if (!row.streetAddress1) return "";

      const { streetAddress1, city, state, zip } = row;

      return [streetAddress1, city, state, zip].filter((s) => s).join(", ");
    },
  },
  {
    field: "lepc.name",
    headerName: "LEPC",
    flex: 0.5,
    valueGetter: (_, row) => row.lepc?.name,
    renderCell: ({ value }) => {
      return (
        <Tooltip title={value}>
          <Typography variant="body2">{value}</Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "hasCredential",
    headerName: "Facility Has Credential",
    flex: 1,
    align: "center",
    valueGetter: (_, row) => (row.facilityCredentials || []).length > 0,
    renderCell: ({ value }) => (value ? <CheckIcon fontSize="small" /> : null),
  },
];

const ADDITIONAL_FILTER_COLUMNS = [
  { header: "Facility ID", key: "customerFacilityId" },
  { header: "City", key: "city" },
  { header: "Zip", key: "zip" },
  { header: "State/Territory", key: "state" },
  { header: "LEPC", key: "lepc.name" },
];

interface CustomerFacilityPickerProps {
  initialSelectedIds: string[];
  open: boolean;
  onSave: (selectedFacilityIds: string[]) => void;
  onCancel: () => void;
  portalJurisdiction?: string | null;
  /**
   * Passed in rather than read from the route: the staff EPCRA drawer opens this from
   * `/staff/credentials/epcra`, which has no `/o/:tenantId`, so `useParams()` searched
   * `tenantId:undefined` and returned nothing.
   */
  tenantId?: string;
}

export const CustomerFacilityPicker: React.FC<CustomerFacilityPickerProps> = ({
  initialSelectedIds,
  open,
  onSave,
  onCancel,
  portalJurisdiction,
  tenantId,
}) => {
  // The rows the user has selected
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedFacilityIds(initialSelectedIds);
  }, [initialSelectedIds]);

  const handleSave = () => {
    onSave(selectedFacilityIds);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="lg"
      title="Select Facilities"
      description="Select one or more facilities to associate with this credential."
      primaryActionButtonProps={{
        startIcon: <CheckIcon />,
        onClick: handleSave,
      }}
    >
      <OmnisearchDataGrid
        persistenceKey="select-facilities"
        isURLDriven={false}
        columns={COLUMNS}
        dataQuery={FACILITIES}
        initialSortModel={[{ field: "facility", sort: "asc" }]}
        defaultSearch={`tenantId:${tenantId}`}
        initialFilters={
          portalJurisdiction ? [{ field: "jurisdiction", value: portalJurisdiction }] : []
        }
        getItems={(data) => data.facilities.items}
        getCount={(data) => data.facilities.count}
        noDataMessage="No facilities have been associated to this credential yet."
        initialPageSize={50}
        additionalFilterColumns={ADDITIONAL_FILTER_COLUMNS}
        onSelectedIdsChanged={(ids) => setSelectedFacilityIds(ids.map((i) => i.toString()))}
        selectedIds={selectedFacilityIds}
        gridSx={{
          minHeight: "300px",
          "& .MuiDataGrid-virtualScroller": {
            minHeight: "200px",
            maxHeight: "350px",
            overflowY: "auto",
          },
        }}
        sx={{
          minWidth: 1000,
          p: 0,
        }}
        showManageColumns={false}
      />
    </Dialog>
  );
};
